import { Fragment, useState } from 'react';
import type { JournalEntry, ReverseEntry } from '@syndiqia/ledger/contracts';
import { errorMessage } from '../../api';
import { Alert, Loading, PageHeader } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useAccounts, useJournal, useLedgerMutation } from '../../ledger-queries';
import { useUnits } from '../../queries';
import { useSyndicate } from '../../session';
import { FinancesNav, today } from './FinancesNav';

export function JournalPage() {
  const { t, money, date, locale } = useI18n();
  const syndicate = useSyndicate();
  const journal = useJournal();
  const accounts = useAccounts();
  const units = useUnits();
  const [open, setOpen] = useState<string | null>(null);
  const reverse = useLedgerMutation<{ id: string } & ReverseEntry, JournalEntry>((api, base, { id, ...input }) =>
    api(`${base}/journal/${id}/reverse`, { method: 'POST', body: input }),
  );

  if (!syndicate.can('ledger.read')) return <Alert tone="info">{t.common.forbidden}</Alert>;
  const accountName = (n: string) => {
    const a = accounts.data?.find((x) => x.number === n);
    return a ? `${n} — ${locale === 'fr' ? a.nameFr : a.nameEn}` : n;
  };
  const unitLabel = (id: string | null) => (id ? (units.data?.find((u) => u.id === id)?.label ?? '…') : '');
  const canReverse = (e: JournalEntry) => syndicate.can('ledger.entry.reverse') && !e.reversedByEntryId && e.kind !== 'reversal' && e.kind !== 'contribution_call';

  return (
    <>
      <PageHeader title={t.finances.title} />
      <FinancesNav />
      <h2>{t.journal.title}</h2>
      {journal.isPending && <Loading label={t.loading} />}
      {journal.isError && <Alert>{errorMessage(journal.error, t)}</Alert>}
      {reverse.isError && <Alert>{errorMessage(reverse.error, t)}</Alert>}
      {journal.data && (
        <table className="table table-stack">
          <thead>
            <tr>
              <th>{t.journal.date}</th>
              <th>{t.journal.description}</th>
              <th className="num">{t.journal.debit}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {journal.data.map((e) => (
              <Fragment key={e.id}>
                <tr className={e.reversedByEntryId ? 'row-muted' : undefined}>
                  <td className="nowrap muted small">{date(e.date)}</td>
                  <td className="cell-title">
                    <span className={`badge badge-kind-${e.kind}`}>{t.journal.kind[e.kind]}</span> {e.description}
                    {e.reversedByEntryId && <span className="muted"> · {t.journal.reversed}</span>}
                  </td>
                  <td className="num" data-label={t.journal.debit}>{money(e.lines.reduce((s, l) => s + l.debitCents, 0))}</td>
                  <td className="nowrap">
                    <button type="button" className="link-button" onClick={() => setOpen(open === e.id ? null : e.id)}>
                      {t.journal.showLines}
                    </button>
                  </td>
                </tr>
                {open === e.id && (
                  <tr className="row-detail">
                    <td colSpan={4}>
                      <table className="table table-compact">
                        <tbody>
                          {e.lines.map((l, i) => (
                            <tr key={i}>
                              <td>{accountName(l.accountNumber)}</td>
                              <td>{l.fundCode}</td>
                              <td>{unitLabel(l.unitId)}</td>
                              <td className="num">{l.debitCents ? money(l.debitCents) : ''}</td>
                              <td className="num">{l.creditCents ? money(l.creditCents) : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {canReverse(e) && (
                        <button
                          type="button"
                          className="link-button"
                          disabled={reverse.isPending}
                          onClick={() => {
                            const reason = window.prompt(t.journal.reason);
                            if (reason?.trim()) reverse.mutate({ id: e.id, date: today(), reason: reason.trim() });
                          }}
                        >
                          {t.journal.reverse}
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
